import { Unzip, UnzipInflate } from "fflate";

const VALID_EXTENSIONS = new Set([".mp4", ".mpeg", ".mp3", ".wav", ".mov"]);

export interface ZipEntry {
  name: string;
  valid: boolean;
}

function getExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot).toLowerCase();
}

export function getMimeType(name: string): string {
  switch (getExtension(name)) {
    case ".mp4":
      return "video/mp4";
    case ".mpeg":
      return "video/mpeg";
    case ".mov":
      return "video/quicktime";
    case ".mp3":
      return "audio/mpeg";
    case ".wav":
      return "audio/wav";
    default:
      return "application/octet-stream";
  }
}

export function isValidVideoExtension(name: string): boolean {
  return VALID_EXTENSIONS.has(getExtension(name));
}

/** Returns true for macOS resource fork / metadata entries that should always be silently ignored. */
function isMacOSMetadata(name: string): boolean {
  if (name.startsWith("__MACOSX/")) return true;
  const basename = name.split("/").pop() ?? "";
  return basename.startsWith("._");
}

/**
 * Streams through the zip from start to finish to collect all file entry names.
 * Does not decompress any content.
 */
export function scanZipEntries(zipFile: File): Promise<ZipEntry[]> {
  return new Promise((resolve, reject) => {
    const entries: ZipEntry[] = [];

    const unzip = new Unzip((file) => {
      if (file.name.endsWith("/")) return; // skip directories
      if (isMacOSMetadata(file.name)) return; // skip macOS resource forks
      entries.push({ name: file.name, valid: isValidVideoExtension(file.name) });
    });
    unzip.register(UnzipInflate);

    const reader = zipFile.stream().getReader();
    const pump = (): void => {
      reader.read().then(({ done, value }) => {
        if (done) {
          try {
            unzip.push(new Uint8Array(0), true);
          } catch {
            // ignore — some zips resolve cleanly before the final push
          }
          resolve(entries);
          return;
        }
        try {
          unzip.push(value, false);
          pump();
        } catch (e) {
          reject(e);
        }
      }).catch(reject);
    };
    pump();
  });
}

/**
 * Fully decompresses one zip entry into a Blob.
 * Used for duration probing — revoke the resulting object URL immediately after reading duration.
 * Cancels the stream as soon as the target entry is fully decompressed.
 */
export function extractZipEntryToBlob(
  zipFile: File,
  entryName: string
): Promise<Blob> {
  const mimeType = getMimeType(entryName);
  return new Promise((resolve, reject) => {
    const chunks: Uint8Array[] = [];
    let found = false;

    const unzip = new Unzip((file) => {
      if (file.name !== entryName) return;
      found = true;
      file.ondata = (err, dat, final) => {
        if (err) {
          reject(err);
          return;
        }
        chunks.push(dat as Uint8Array);
        if (final) {
          resolve(new Blob(chunks as BlobPart[], { type: mimeType }));
          // Cancel the stream — no need to read the rest of the zip
          reader.cancel().catch(() => {});
        }
      };
      file.start();
    });
    unzip.register(UnzipInflate);

    const reader = zipFile.stream().getReader();
    const pump = (): void => {
      reader.read().then(({ done, value }) => {
        if (done) {
          if (!found) reject(new Error(`Entry "${entryName}" not found in zip`));
          return;
        }
        try {
          unzip.push(value, false);
          pump();
        } catch (e) {
          reject(e);
        }
      }).catch((e) => {
        // reader.cancel() causes a rejection we can safely ignore if already resolved
        if (chunks.length > 0) return;
        reject(e);
      });
    };
    pump();
  });
}

/**
 * Streams a zip entry directly to S3 via presigned multipart upload URLs.
 * Accumulates decompressed chunks until partSize bytes, then PUTs each part.
 * Returns ETags sorted by part number for the PUT /uploadEvidence confirmation.
 */
export async function streamZipEntryToS3(
  zipFile: File,
  entryName: string,
  presignedUrls: string[],
  partSize: number,
  onProgress: (loaded: number, total: number) => void
): Promise<Array<{ partNumber: number; eTag: string | null }>> {
  const mimeType = getMimeType(entryName);
  const partsCount = presignedUrls.length;
  const partsInfo: Array<{ partNumber: number; eTag: string | null }> = [];
  let partIndex = 0;
  let bytesUploaded = 0;
  const estimatedTotal = partSize * partsCount;

  // Synchronous accumulator filled by fflate ondata callbacks
  const accumulated: Uint8Array[] = [];
  let accumulatedSize = 0;
  let decompressError: Error | null = null;
  let foundEntry = false;
  let entryDone = false;

  const unzip = new Unzip((file) => {
    if (file.name !== entryName) return;
    foundEntry = true;
    file.ondata = (err, dat, final) => {
      if (err) {
        decompressError = err;
        return;
      }
      accumulated.push(dat);
      accumulatedSize += dat.byteLength;
      if (final) entryDone = true;
    };
    file.start();
  });
  unzip.register(UnzipInflate);

  const mergeAccumulated = (size: number): Uint8Array => {
    const out = new Uint8Array(size);
    let off = 0;
    for (const c of accumulated) {
      out.set(c, off);
      off += c.byteLength;
    }
    accumulated.length = 0;
    accumulatedSize = 0;
    return out;
  };

  const uploadPart = async (data: Uint8Array): Promise<void> => {
    const resp = await fetch(presignedUrls[partIndex], {
      method: "PUT",
      headers: { "Content-Type": mimeType },
      body: data.buffer as ArrayBuffer,
    });
    if (!resp.ok) {
      throw new Error(`S3 part ${partIndex + 1} upload failed: ${resp.status}`);
    }
    const eTag = resp.headers.get("etag") || resp.headers.get("ETag") || null;
    partsInfo.push({ partNumber: partIndex + 1, eTag });
    bytesUploaded += data.byteLength;
    onProgress(bytesUploaded, estimatedTotal);
    partIndex++;
  };

  const reader = zipFile.stream().getReader();
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    unzip.push(value, false);
    if (decompressError) throw decompressError;

    // Flush all complete parts except the last (which may not be a full partSize)
    while (accumulatedSize >= partSize && partIndex < partsCount - 1) {
      const merged = mergeAccumulated(accumulatedSize);
      const partData = merged.slice(0, partSize);
      const remainder = merged.slice(partSize);
      if (remainder.length > 0) {
        accumulated.push(remainder);
        accumulatedSize = remainder.length;
      }
      await uploadPart(partData);
    }

    // Target entry is fully decompressed — no need to read further zip data
    if (entryDone) {
      reader.cancel().catch(() => {});
      break;
    }
  }

  // Final push to flush any remaining fflate internal state
  try {
    unzip.push(new Uint8Array(0), true);
  } catch {
    // ignore
  }
  if (decompressError) throw decompressError;

  // Upload the remaining bytes as the final part
  if (accumulatedSize > 0) {
    await uploadPart(mergeAccumulated(accumulatedSize));
  }

  if (!foundEntry) {
    throw new Error(`Entry "${entryName}" was not found in zip archive`);
  }

  return partsInfo.sort((a, b) => a.partNumber - b.partNumber);
}
